import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Tooltip, FormControlLabel, Switch, Grid, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RateReviewIcon from '@mui/icons-material/RateReview';
import DraftsIcon from '@mui/icons-material/Drafts';
import OutboxIcon from '@mui/icons-material/Outbox';
import TerminalIcon from '@mui/icons-material/Terminal';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import ReplayIcon from '@mui/icons-material/Replay';

import KpiCard from '../components/reports/KpiCard';
import {
  getEmailQueueStatusCounts,
  listEmailQueueMonitor,
  cancelEmailQueueItem,
  sendQueuedEmailNow,
  pauseEmailWorker,
  resumeEmailWorker,
  queryLogs,
  unsubscribeLead,
  unsubscribeLeadContact,
} from '../services/api';

const POLL_MS = 10000;
const LOG_QUERY = 'Scheduled email';
const LOG_LEVELS = 'Information,Warning,Error,Fatal';
const LOG_LIMIT = 100;

const LEVEL_COLOR = {
  Information: 'info',
  Warning: 'warning',
  Error: 'error',
  Fatal: 'error',
};

function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function relativeFrom(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const sec = Math.round(abs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  let txt;
  if (sec < 60) txt = `${sec}s`;
  else if (min < 60) txt = `${min}m`;
  else if (hr < 24) txt = `${hr}h`;
  else txt = `${day}d`;
  return diff >= 0 ? `in ${txt}` : `${txt} ago`;
}

export default function EmailQueueMonitorPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [sent, setSent] = useState([]);
  const [failed, setFailed] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, sch, sn, fl] = await Promise.all([
        getEmailQueueStatusCounts(),
        listEmailQueueMonitor('Scheduled', 50),
        listEmailQueueMonitor('Sent', 50),
        listEmailQueueMonitor('Failed', 50),
      ]);
      setCounts(c.data);
      setScheduled(sch.data || []);
      setSent(sn.data || []);
      setFailed(fl.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to load queue status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await queryLogs({
        level: LOG_LEVELS,
        q: LOG_QUERY,
        take: LOG_LIMIT,
      });
      setLogs(data?.items || []);
    } catch (err) {
      // Logs is admin-only; ignore 403 silently
      if (err.response?.status !== 403) {
        setError(prev => prev || (err.response?.data || err.message || 'Failed to load logs'));
      }
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchAll();
    fetchLogs();
  }, [fetchAll, fetchLogs]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(refreshAll, POLL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, refreshAll]);

  const handleSendNow = async (item) => {
    if (!window.confirm(`Send now to ${item.recipientEmail || '(no recipient)'}?`)) return;
    setBusyId(item.id);
    setInfo('');
    setError('');
    try {
      const { data } = await sendQueuedEmailNow(item.id);
      if (data?.success === false) {
        setError(data.errorMessage || 'Send failed');
      } else {
        setInfo('Email sent');
      }
      await fetchAll();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.errorMessage || data || err.message || 'Send failed');
    } finally {
      setBusyId(null);
    }
  };

  const handlePauseResume = async () => {
    if (!counts) return;
    const paused = counts.workerPaused;
    if (!paused && !window.confirm('Pause the scheduler? Queued emails will not be sent until resumed.')) return;
    setInfo('');
    setError('');
    try {
      if (paused) {
        await resumeEmailWorker();
        setInfo('Worker resumed');
      } else {
        await pauseEmailWorker();
        setInfo('Worker paused');
      }
      await fetchAll();
    } catch (err) {
      setError(err.response?.data || err.message || 'Worker control failed');
    }
  };

  const handleRetry = async (item) => {
    if (!window.confirm(`Retry sending to ${item.recipientEmail || '(no recipient)'}?`)) return;
    setBusyId(item.id);
    setInfo('');
    setError('');
    try {
      const { data } = await sendQueuedEmailNow(item.id);
      if (data?.success === false) {
        setError(data.errorMessage || 'Retry failed');
      } else {
        setInfo('Retry sent');
      }
      await fetchAll();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.errorMessage || data || err.message || 'Retry failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (item) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    setBusyId(item.id);
    setInfo('');
    setError('');
    try {
      await cancelEmailQueueItem(item.id);
      setInfo('Scheduled email cancelled');
      await fetchAll();
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to cancel');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelAndUnsubscribe = async (item) => {
    const hasContact = Boolean(item.contactId);
    const targetLabel = hasContact
      ? `contact ${item.contactName || item.recipientEmail || `#${item.contactId}`}`
      : `lead ${item.leadCompanyName || `#${item.leadId}`}`;
    const reason = window.prompt(
      `Suppress all outreach to ${targetLabel} and cancel pending emails. Reason (optional):`,
      ''
    );
    if (reason === null) return;
    setBusyId(item.id);
    setInfo('');
    setError('');
    try {
      const { data } = hasContact
        ? await unsubscribeLeadContact(item.leadId, item.contactId, reason || null)
        : await unsubscribeLead(item.leadId, reason || null);
      setInfo(`Unsubscribed ${hasContact ? 'contact' : 'lead'}. Cancelled ${data?.cancelledInteractionCount ?? 0} pending email(s).`);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data || err.message || 'Unsubscribe failed');
    } finally {
      setBusyId(null);
    }
  };

  const openLead = (leadId) => navigate(`/leads/${leadId}`);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Email Queue Monitor</Typography>
          <Typography variant="caption" color="text.secondary">
            Scheduler worker status, queue counts, upcoming sends, recent delivery and worker log.
            {lastRefresh && ` Last refresh: ${lastRefresh.toLocaleTimeString()}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {counts && (
            counts.workerPaused ? (
              <Button startIcon={<PlayCircleIcon />} variant="contained" color="success" size="small"
                onClick={handlePauseResume}>
                Resume Worker
              </Button>
            ) : (
              <Button startIcon={<PauseCircleIcon />} variant="outlined" color="warning" size="small"
                onClick={handlePauseResume}>
                Pause Worker
              </Button>
            )
          )}
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} size="small" />}
            label={<Typography variant="body2">Auto-refresh (10s)</Typography>}
          />
          <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={refreshAll} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{String(error)}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      {/* Daily Budget Banner */}
      {counts && (
        <Paper sx={{
          p: 2, mb: 2,
          borderLeft: '4px solid',
          borderColor: counts.dailyRemaining <= 0 ? 'error.main'
            : counts.dailyRemaining < 20 ? 'warning.main' : 'success.main',
          bgcolor: counts.dailyRemaining <= 0 ? 'error.50'
            : counts.dailyRemaining < 20 ? 'warning.50' : 'background.paper',
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Daily Send Budget (UTC)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {counts.sentToday} / {counts.dailySendLimit} sent
                {' '}
                <Typography component="span" variant="body2" color={counts.dailyRemaining <= 0 ? 'error' : 'success.main'}>
                  ({counts.dailyRemaining <= 0 ? 'LIMIT REACHED — worker paused for today' : `${counts.dailyRemaining} remaining`})
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Resets at {fmt(counts.dailyResetsAtUtc)} · Config: <code>Email:DailySendLimit</code>
              </Typography>
            </Box>
            <Box sx={{ minWidth: 160 }}>
              <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                <Box sx={{
                  width: `${Math.min(100, (counts.sentToday / Math.max(1, counts.dailySendLimit)) * 100)}%`,
                  height: '100%',
                  bgcolor: counts.dailyRemaining <= 0 ? 'error.main'
                    : counts.dailyRemaining < 20 ? 'warning.main' : 'success.main',
                  transition: 'width 0.3s',
                }} />
              </Box>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* KPI Row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Draft" value={counts?.draft ?? '—'} tone="neutral" icon={DraftsIcon} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Scheduled" value={counts?.scheduled ?? '—'} tone="info" icon={ScheduleIcon}
            sub={counts?.nextScheduledFor ? `Next ${relativeFrom(counts.nextScheduledFor)}` : 'No upcoming'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Ready to Send" value={counts?.readyToSend ?? '—'} tone="warning" icon={OutboxIcon}
            sub="Worker dispatches next tick" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Sent" value={counts?.sent ?? '—'} tone="positive" icon={MarkEmailReadIcon} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Failed" value={counts?.failed ?? '—'} tone="negative" icon={ErrorOutlineIcon}
            sub={counts?.failedRetryable != null ? `${counts.failedRetryable} retryable` : null} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Needs Review" value={counts?.needsReview ?? '—'} tone="warning" icon={RateReviewIcon}
            sub="Skipped by worker" />
        </Grid>
      </Grid>

      {/* Worker Config Strip */}
      <Paper sx={{ p: 1.5, mb: 2, bgcolor: counts?.workerPaused ? 'warning.50' : 'grey.50',
        borderLeft: counts?.workerPaused ? '4px solid' : 'none', borderColor: 'warning.main' }}>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            <strong>Worker:</strong> ScheduledEmailWorker
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Status: {counts ? (
              counts.workerPaused
                ? <Chip size="small" color="warning" label={`Paused${counts.workerPausedBy ? ' by ' + counts.workerPausedBy : ''}`} sx={{ height: 18 }} />
                : <Chip size="small" color="success" label="Polling" sx={{ height: 18 }} />
            ) : <Chip size="small" label="Unknown" sx={{ height: 18 }} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Interval: <strong>{counts?.workerIntervalSeconds ?? '—'}s</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Batch: <strong>{counts?.workerBatchSize ?? '—'}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Max retries: <strong>{counts?.workerMaxRetries ?? '—'}</strong>
            {counts?.workerRetryCooldownMinutes != null && ` (cooldown ${counts.workerRetryCooldownMinutes}m)`}
          </Typography>
          {counts?.workerLastTickAt && (
            <Typography variant="caption" color="text.secondary">
              Last tick: <strong>{fmt(counts.workerLastTickAt)}</strong>
              {' — '}
              {counts.workerLastTickProcessed} processed
              {counts.workerLastTickSent > 0 && `, ${counts.workerLastTickSent} sent`}
              {counts.workerLastTickFailed > 0 && `, ${counts.workerLastTickFailed} failed`}
            </Typography>
          )}
          {counts && (
            <Typography variant="caption" color={counts.dailyRemaining <= 0 ? 'error' : 'text.secondary'}>
              Daily budget: <strong>{counts.sentToday}/{counts.dailySendLimit}</strong>
              {' '}
              <Chip size="small"
                color={counts.dailyRemaining <= 0 ? 'error' : (counts.dailyRemaining < 20 ? 'warning' : 'success')}
                label={counts.dailyRemaining <= 0 ? 'Limit reached' : `${counts.dailyRemaining} left`}
                sx={{ height: 18 }} />
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Upcoming Scheduled */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Upcoming Scheduled <Chip size="small" label={scheduled.length} sx={{ ml: 1, height: 18 }} />
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Scheduled For</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduled.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No scheduled emails.
                  </TableCell>
                </TableRow>
              ) : (
                scheduled.map(item => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => openLead(item.leadId)}>
                        {item.leadCompanyName || `Lead #${item.leadId}`}
                      </Typography>
                      {item.contactName && (
                        <Typography variant="caption" color="text.secondary">{item.contactName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.recipientEmail || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={item.subject}>{item.subject || '(no subject)'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmt(item.scheduledFor)}</Typography>
                      <Typography variant="caption" color="text.secondary">{relativeFrom(item.scheduledFor)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{item.emailProviderName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.needsReview && <Chip size="small" color="warning" label="NeedsReview" sx={{ height: 18 }} />}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Send now">
                        <span>
                          <IconButton size="small" onClick={() => handleSendNow(item)}
                            disabled={busyId === item.id || !item.recipientEmail}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={item.contactId ? 'Unsubscribe contact + cancel pending' : 'Unsubscribe lead + cancel pending'}>
                        <span>
                          <IconButton size="small" color="warning" onClick={() => handleCancelAndUnsubscribe(item)}
                            disabled={busyId === item.id}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleCancel(item)}
                            disabled={busyId === item.id}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recently Sent */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Recently Sent <Chip size="small" label={sent.length} sx={{ ml: 1, height: 18 }} />
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sent At</TableCell>
                <TableCell>Lead</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Delivery</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sent.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No sent emails yet.
                  </TableCell>
                </TableRow>
              ) : (
                sent.map(item => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption">{fmt(item.sentDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ cursor: 'pointer' }}
                        onClick={() => openLead(item.leadId)}>
                        {item.leadCompanyName || `Lead #${item.leadId}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.recipientEmail || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={item.subject}>{item.subject || '(no subject)'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{item.emailProviderName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.deliveryState
                        ? <Chip size="small" label={item.deliveryState} sx={{ height: 18 }} />
                        : <Chip size="small" color="success" label="Sent" sx={{ height: 18 }} />}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Failures */}
      {failed.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
              Recent Failures <Chip size="small" color="error" label={failed.length} sx={{ ml: 1, height: 18 }} />
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Last Attempt</TableCell>
                  <TableCell>Lead</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Retries</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failed.map(item => {
                  const max = counts?.workerMaxRetries ?? 3;
                  const retries = item.retryCount ?? 0;
                  const exhausted = retries >= max;
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">{fmt(item.lastAttemptAt || item.sentDate || item.scheduledFor)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ cursor: 'pointer' }}
                          onClick={() => openLead(item.leadId)}>
                          {item.leadCompanyName || `Lead #${item.leadId}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.recipientEmail || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={exhausted ? 'error' : 'warning'}
                          label={`${retries}/${max}`} sx={{ height: 18 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error">{item.lastError || '(unknown)'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={exhausted ? 'Retry budget exhausted — manual retry will reset attempt' : 'Retry now'}>
                          <span>
                            <IconButton size="small" onClick={() => handleRetry(item)}
                              disabled={busyId === item.id || !item.recipientEmail}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Worker Logs */}
      <Paper>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TerminalIcon fontSize="small" /> Worker Logs
            <Chip size="small" label={logs.length} sx={{ ml: 1, height: 18 }} />
          </Typography>
          {logsLoading && <CircularProgress size={16} />}
        </Box>
        <Divider />
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 170 }}>Time</TableCell>
                <TableCell sx={{ width: 110 }}>Level</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    {logsLoading ? 'Loading…' : 'No worker logs in window. Admin role required.'}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(entry => (
                  <TableRow key={entry.id} hover>
                    <TableCell>
                      <Typography variant="caption">{fmt(entry.timestamp)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={LEVEL_COLOR[entry.level] || 'default'} label={entry.level} sx={{ height: 18 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {entry.message}
                      </Typography>
                      {entry.exception && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                          {String(entry.exception).split('\n')[0]}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
